import { Box, Drawer, IconButton, LinearProgress, Stack, Typography } from "@mui/material";
import { Close, ImageNotSupported, Visibility } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";

/**
 * Generic file preview drawer used by both /invoices and /pending-review.
 *
 * Renders the file inline (image or PDF iframe) by:
 *   1. Resolving the storage URL via `api.storage.getUrl`
 *   2. Fetching the bytes through the nginx /convex proxy (avoids redirect
 *      issues with Convex self-hosted storage going through the reverse proxy)
 *   3. Building a blob URL so <img>/<iframe> can load without CORS games
 *
 * For non-renderable types (csv, ofx, html, …) it falls back to a download link.
 */

/** Rewrite Convex storage URLs to go through the local /convex nginx alias
 *  so the browser stays same-origin.
 *
 *  Self-hosted Convex returns RELATIVE URLs like "/api/storage/<uuid>" — those
 *  must be prefixed with "/convex" or the browser would resolve them against
 *  the frontend origin (which doesn't host /api/storage/*) and 404. Cloud
 *  Convex returns absolute URLs against a separate hostname; those also get
 *  rewritten to the same /convex path so we never hit CORS.
 */
function proxyStorageUrl(url: string): string {
  // Self-hosted Convex returns relative paths like "/api/storage/<uuid>".
  // Prepend /convex so the request hits the nginx alias instead of being
  // resolved against the frontend origin (which would 404).
  if (url.startsWith("/api/")) {
    return `/convex${url}`;
  }
  // Cloud Convex case: absolute URL on a different hostname → rewrite to
  // same-origin /convex/<path>.
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== window.location.hostname) {
      return `/convex${parsed.pathname}${parsed.search}`;
    }
    return url;
  } catch {
    return url;
  }
}

function useBlobUrl(storageUrl: string | null | undefined) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!storageUrl) {
      setBlobUrl(null);
      return;
    }
    let revoked = false;
    setLoading(true);
    setError(false);

    const proxied = proxyStorageUrl(storageUrl);
    fetch(proxied)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (revoked) return;
        setBlobUrl(URL.createObjectURL(blob));
        setLoading(false);
      })
      .catch(() => {
        if (!revoked) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      revoked = true;
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [storageUrl]);

  return { blobUrl, loading, error };
}

export interface FilePreviewDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Convex `_storage` id of the file to preview, or null when nothing selected. */
  storageId: string | null;
  /** Bold header line (e.g. file name or vendor name). */
  title: string;
  /** Smaller secondary line (e.g. file category, "Invoice #1234", canonical name). */
  subtitle?: string;
}

export function FilePreviewDrawer({ open, onClose, storageId, title, subtitle }: FilePreviewDrawerProps) {
  // Only query when we have a storage id and the drawer is open
  const rawUrl = useQuery(
    api.storage.getUrl,
    storageId && open ? { storageId: storageId as any } : "skip",
  );
  const metadata = useQuery(
    api.storage.getMetadata,
    storageId && open ? { storageId: storageId as any } : "skip",
  );

  const { blobUrl, loading: blobLoading, error: blobError } = useBlobUrl(
    rawUrl && open ? rawUrl : null,
  );
  const contentType = metadata?.contentType ?? "";
  const isImage = contentType.startsWith("image/");
  const isPdf = contentType === "application/pdf";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: "50vw" }, bgcolor: "var(--c-bg-elevated)" },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 2, borderBottom: "1px solid var(--c-overlay-6)" }}
      >
        <Box sx={{ minWidth: 0, flex: 1, mr: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, p: 2, overflow: "auto" }}>
        {!storageId ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <ImageNotSupported sx={{ fontSize: 48, color: "var(--c-text-muted)", mb: 2 }} />
            <Typography color="text.secondary">No preview available</Typography>
            <Typography variant="caption" color="text.disabled">
              This row has no attached document.
            </Typography>
          </Box>
        ) : rawUrl === undefined || blobLoading ? (
          <Box sx={{ py: 4 }}>
            <LinearProgress />
          </Box>
        ) : blobError || !blobUrl ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <ImageNotSupported sx={{ fontSize: 48, color: "var(--c-text-muted)", mb: 2 }} />
            <Typography color="text.secondary">File not found</Typography>
          </Box>
        ) : isImage ? (
          <Box
            component="img"
            src={blobUrl}
            alt={title}
            sx={{ width: "100%", borderRadius: 1, border: "1px solid var(--c-overlay-6)" }}
          />
        ) : isPdf ? (
          <Box
            component="iframe"
            src={blobUrl}
            sx={{
              width: "100%",
              height: "calc(100vh - 120px)",
              border: "1px solid var(--c-overlay-6)",
              borderRadius: 1,
              bgcolor: "#fff",
            }}
            title={title}
          />
        ) : (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Visibility sx={{ fontSize: 48, color: "var(--c-text-muted)", mb: 2 }} />
            <Typography color="text.secondary">Preview not available for this file type</Typography>
            <Typography variant="caption" color="text.disabled">
              {contentType || "Unknown type"}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--c-link-accent)" }}
              >
                Download file
              </a>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
